import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  KeyRound,
  PackageCheck,
  Rocket,
  ShieldCheck,
  Smartphone,
  Store,
  Users,
  X,
} from 'lucide-react';

interface ProgrammationMobileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function StatusPill({ children, tone }: { children: React.ReactNode; tone: 'green' | 'amber' | 'blue' | 'gray' }) {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    amber: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    gray: 'bg-slate-100 text-slate-600 ring-slate-500/20',
  };
  return <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${tones[tone]}`}>{children}</span>;
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="rounded-2xl bg-white p-4 ring-1 ring-black/5"><dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</dt><dd className="mt-1.5 text-sm font-semibold leading-6 text-slate-900">{value}</dd></div>;
}

function Section({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return <section className="rounded-[28px] bg-white p-5 shadow-[0_18px_55px_rgba(15,23,42,0.08)] ring-1 ring-black/5 md:p-7">
    <h3 className="mb-5 flex items-center gap-3 text-xl font-semibold tracking-tight text-[#1d1d1f]"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f5f5f7]"><Icon className="h-5 w-5 text-[#0071e3]" /></span>{title}</h3>
    {children}
  </section>;
}

const publicationSteps = [
  ['1', 'Préparer', 'Valider l’identifiant de l’application, les certificats, les versions minimales, les icônes, les captures d’écran et les textes de fiche.'],
  ['2', 'Tester', 'Exécuter les tests fonctionnels, sécurité, confidentialité, performance et compatibilité sur de vrais appareils Android et Apple.'],
  ['3', 'Signer', 'Utiliser les certificats et profils autorisés. Ne jamais stocker une clé de signature ou un secret dans le dépôt source.'],
  ['4', 'Soumettre', 'Créer la version dans la console appropriée, joindre les métadonnées et documenter le responsable de la soumission.'],
  ['5', 'Valider', 'Traiter les avertissements ou refus des boutiques, puis obtenir une validation humaine avant toute mise en production.'],
  ['6', 'Exploiter', 'Suivre les versions, renouvellements, accès, statistiques, incidents et mises à jour obligatoires des politiques.'],
];

export default function ProgrammationMobileModal({ isOpen, onClose }: ProgrammationMobileModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (document.activeElement === dialogRef.current || !dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [isOpen, onClose]);

  return <AnimatePresence>{isOpen && <motion.div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-3 backdrop-blur-xl md:p-6"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    onMouseDown={onClose}
  >
    <motion.div
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="programmation-mobile-title"
      onMouseDown={(event) => event.stopPropagation()}
      initial={{ y: 28, scale: 0.98, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} exit={{ y: 20, scale: 0.98, opacity: 0 }}
      className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-[32px] bg-[#f5f5f7] font-sans text-[#1d1d1f] shadow-[0_35px_120px_rgba(0,0,0,0.45)] outline-none"
    >
      <header className="relative overflow-hidden bg-black px-6 py-7 text-white md:px-10 md:py-9">
        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#0071e3]/25 blur-3xl" />
        <div className="relative flex items-start justify-between gap-5">
          <div className="flex items-start gap-4 md:gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] bg-white text-black md:h-16 md:w-16"><Smartphone className="h-7 w-7 md:h-8 md:w-8" /></div>
            <div><p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#2997ff]">Programmation & IA · Capacité mobile</p><h2 id="programmation-mobile-title" className="mt-2 text-3xl font-semibold tracking-[-0.03em] md:text-5xl">Programmation mobile</h2><p className="mt-3 max-w-3xl text-sm leading-6 text-white/70 md:text-base">Gouvernance des comptes développeur, accès aux boutiques et cadre de publication pour livrer des applications Android et Apple de façon maîtrisée.</p></div>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-[#2997ff]"><X className="h-5 w-5" /></button>
        </div>
      </header>

      <div className="overflow-y-auto px-4 py-5 md:px-8 md:py-8">
        <div className="grid gap-5 lg:grid-cols-2">
          <section className="overflow-hidden rounded-[28px] bg-[#202124] text-white shadow-[0_18px_55px_rgba(15,23,42,0.14)]">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5"><div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#3ddc84]"><Smartphone className="h-5 w-5 text-[#073b21]" /></span><div><p className="text-xs uppercase tracking-[0.18em] text-white/50">Écosystème</p><h3 className="text-xl font-semibold">Android</h3></div></div><StatusPill tone="green">Compte créé</StatusPill></div>
            <div className="space-y-5 p-6">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Detail label="Compte principal" value="programmeur@bzinc.ca" />
                <Detail label="Frais" value="Payés le 18 août 2026" />
              </dl>
              <div className="rounded-2xl bg-white/5 p-4 text-sm leading-6 text-white/70 ring-1 ring-white/10"><strong className="text-white">Statut financier :</strong> les frais de création du compte ont été payés le 18 août 2026. Aucun montant n’est documenté dans l’information fournie; il ne doit donc pas être inventé.</div>
              <p className="text-sm leading-6 text-white/70">Ce compte constitue le point d’ancrage BZ pour les publications Android. Les rôles, moyens de récupération, applications rattachées et changements de propriétaires doivent être consignés sans exposer de secrets.</p>
            </div>
          </section>

          <section className="overflow-hidden rounded-[28px] bg-white shadow-[0_18px_55px_rgba(15,23,42,0.10)] ring-1 ring-black/5">
            <div className="flex items-center justify-between border-b border-black/5 px-6 py-5"><div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-full bg-black"><Store className="h-5 w-5 text-white" /></span><div><p className="text-xs uppercase tracking-[0.18em] text-slate-400">Écosystème</p><h3 className="text-xl font-semibold">Apple Developer</h3></div></div><StatusPill tone="amber">Abonnement annulé</StatusPill></div>
            <div className="space-y-5 p-6">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Detail label="Compte principal" value="eric.durand@bzinc.ca" />
                <Detail label="Accès additionnel" value="Marc-Antoine Caron" />
                <Detail label="Souscription" value="18 août 2026" />
                <Detail label="Tarif documenté" value="120 $ US par année" />
              </dl>
              <div className="rounded-2xl bg-amber-50 p-4 text-sm leading-6 text-amber-900 ring-1 ring-amber-600/15"><strong>Situation actuelle :</strong> l’abonnement annuel Apple Developer pris le 18 août 2026 est annulé. Avant une soumission App Store, confirmer l’état réel de l’adhésion et la capacité de signer ou publier une application.</div>
              <a href="https://appstoreconnect.apple.com/" target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#0071e3] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0077ed] focus:outline-none focus:ring-2 focus:ring-[#0071e3] focus:ring-offset-2">Ouvrir App Store Connect <ExternalLink className="h-4 w-4" /></a>
            </div>
          </section>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.1fr_.9fr]">
          <Section title="Gouvernance des accès" icon={ShieldCheck}>
            <div className="grid gap-3 md:grid-cols-2">
              {[
                [Users, 'Responsabilité claire', 'Un propriétaire principal par compte et des accès nominatifs. Aucun compte partagé entre plusieurs personnes.'],
                [KeyRound, 'Secrets hors documentation', 'Ne jamais inscrire dans PRIsME un mot de passe, un code de récupération, une clé de signature, un certificat privé, un code 2FA ou un secret.'],
                [CalendarDays, 'Revue trimestrielle', 'Confirmer les membres actifs, les rôles, la récupération des comptes, les abonnements et les appareils de confiance.'],
                [CreditCard, 'Suivi financier', 'Conserver les preuves de paiement et dates de renouvellement dans le système financier approprié, sans données de carte dans PRIsME.'],
              ].map(([Icon, title, text]) => {
                const IconComponent = Icon as React.ElementType;
                return <div key={title as string} className="rounded-2xl bg-[#f5f5f7] p-4"><IconComponent className="h-5 w-5 text-[#0071e3]" /><h4 className="mt-3 font-semibold text-slate-900">{title as string}</h4><p className="mt-1 text-sm leading-6 text-slate-600">{text as string}</p></div>;
              })}
            </div>
          </Section>

          <Section title="État de préparation" icon={PackageCheck}>
            <div className="space-y-3">
              {[
                ['Android', 'Compte créé et frais payés', 'green'],
                ['Apple', 'Compte créé, accès Eric + Marc-Antoine', 'blue'],
                ['Apple Developer', 'Adhésion annuelle annulée', 'amber'],
                ['Documentation', 'Inventaire à maintenir dans PRIsME', 'gray'],
              ].map(([name, status, tone]) => <div key={name} className="flex items-center justify-between gap-3 rounded-2xl bg-[#f5f5f7] p-4"><div><p className="font-semibold text-slate-900">{name}</p><p className="mt-0.5 text-xs text-slate-500">{status}</p></div><StatusPill tone={tone as 'green' | 'amber' | 'blue' | 'gray'}>{tone === 'green' ? 'Prêt' : tone === 'amber' ? 'À régulariser' : tone === 'blue' ? 'Configuré' : 'À suivre'}</StatusPill></div>)}
            </div>
          </Section>
        </div>

        <div className="mt-6"><Section title="Cycle de publication" icon={Rocket}>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{publicationSteps.map(([number, title, description]) => <article key={number} className="rounded-2xl bg-[#f5f5f7] p-5"><div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-sm font-semibold text-white">{number}</div><h4 className="mt-4 text-lg font-semibold tracking-tight text-slate-900">{title}</h4><p className="mt-2 text-sm leading-6 text-slate-600">{description}</p></article>)}</div>
        </Section></div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Section title="Prochaines actions recommandées" icon={CheckCircle2}>
            <ul className="space-y-3 text-sm leading-6 text-slate-700">
              {[
                'Confirmer le nom officiel et le type du compte Android ainsi que les rôles actuellement accordés.',
                'Centraliser l’inventaire des applications, identifiants de bundle/package et responsables techniques.',
                'Valider si l’adhésion Apple Developer doit être réactivée avant le prochain projet iOS.',
                'Confirmer le rôle exact de Marc-Antoine Caron dans App Store Connect et appliquer le moindre privilège.',
                'Documenter un propriétaire de relève et une procédure de récupération pour chaque compte.',
                'Mettre une revue trimestrielle des accès, abonnements, certificats et échéances.',
              ].map((item) => <li key={item} className="flex gap-3"><CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-600" /><span>{item}</span></li>)}
            </ul>
          </Section>

          <Section title="Points de vigilance" icon={AlertTriangle}>
            <ul className="space-y-3 text-sm leading-6 text-slate-700">
              {[
                'Une adhésion Apple annulée peut bloquer la signature, la soumission ou la mise à jour d’une application.',
                'Les comptes développeur doivent utiliser l’authentification multifacteur et des mécanismes de récupération contrôlés.',
                'Les certificats, profils et clés de signature sont des actifs sensibles avec une durée de vie à surveiller.',
                'Toute publication doit être approuvée par un humain responsable et laisser une trace de version et de décision.',
                'Les exigences de confidentialité des boutiques doivent être validées avant chaque soumission.',
              ].map((item) => <li key={item} className="flex gap-3"><AlertTriangle className="mt-1 h-4 w-4 shrink-0 text-amber-600" /><span>{item}</span></li>)}
            </ul>
          </Section>
        </div>
      </div>
    </motion.div>
  </motion.div>}</AnimatePresence>;
}
